import { robotDataSlice } from '../redux/RobotDataSlice';
import { robotSlice } from '../redux/RobotSlice';
import type { AppDispatch } from '../redux/Store';
import { ROBOTS, RobotActions } from '../utils/constants';

/** Subset of the SDK `getFirmwareVersions` payload used for update UI state. */
export type FirmwareVersionsPayload = {
  state?: string;
  criticalUpdate?: boolean;
} & Record<string, unknown>;

export type FirmwareStatusInput = {
  updaterState: FirmwareVersionsPayload | null;
  versionMetadata: Record<string, unknown> | null;
};

type ReduxStoreLike = { dispatch: AppDispatch };

type FirmwareStatusEvaluation = {
  status: string;
  needsDownload: boolean;
  needsInstall: boolean;
  criticalUpdate: boolean;
};

type FirmwareTriplet = {
  major: number;
  minor: number;
  revision: number;
};

type AssetVersionEntry = {
  local?: unknown;
  active?: unknown;
  staged?: unknown;
};

type FirmwareType = 'bootloader' | 'main_app' | 'coprocessor';

const FIRMWARE_TYPES: FirmwareType[] = ['bootloader', 'main_app', 'coprocessor'];

const EXPECTED_VERSION_CONTAINER_KEYS = [
  'assetVersionsByType',
  'assetFirmwareVersionsByType',
  'firmwareVersionByType',
  'firmwareVersionsByType',
  'expectedVersionsByType',
  'availableVersionsByType',
  'assetVersions',
];

function isNonEmptyUpdaterPhase(phase: unknown): phase is string {
  return typeof phase === 'string' && phase.trim() !== '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseVersionPart(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
    return value;
  }

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }

  return null;
}

function parseTripletFromString(value: string): FirmwareTriplet | null {
  const match = value.trim().match(/^v?(\d+)\.(\d+)\.(\d+)$/i);
  if (!match) {
    return null;
  }

  return {
    major: Number(match[1]),
    minor: Number(match[2]),
    revision: Number(match[3]),
  };
}

function parseFirmwareTriplet(value: unknown): FirmwareTriplet | null {
  if (typeof value === 'string') {
    return parseTripletFromString(value);
  }

  if (!isRecord(value)) {
    return null;
  }

  const directMajor = parseVersionPart(value.major);
  const directMinor = parseVersionPart(value.minor);
  const directRevision = parseVersionPart(value.revision);
  if (directMajor !== null && directMinor !== null && directRevision !== null) {
    return {
      major: directMajor,
      minor: directMinor,
      revision: directRevision,
    };
  }

  for (const nestedKey of ['version', 'firmwareVersion', 'assetVersion']) {
    const nestedTriplet = parseFirmwareTriplet(value[nestedKey]);
    if (nestedTriplet !== null) {
      return nestedTriplet;
    }
  }

  return null;
}

function getAssetSlotVersion(slot: unknown, expectedAssetId: string | null): FirmwareTriplet | null {
  if (!isRecord(slot) || slot.state !== 'present') {
    return null;
  }

  if (expectedAssetId !== null && typeof slot.assetId === 'string' && slot.assetId !== expectedAssetId) {
    return null;
  }

  return parseFirmwareTriplet(slot.version);
}

function normalizeFirmwareType(value: string): FirmwareType | null {
  switch (value) {
    case 'bootloader':
    case 'bl':
      return 'bootloader';
    case 'main_app':
    case 'mainApp':
    case 'main':
      return 'main_app';
    case 'coprocessor':
    case 'coProcessor':
    case 'cp':
      return 'coprocessor';
    default:
      return null;
  }
}

function extractVersionMap(container: unknown): Partial<Record<FirmwareType, FirmwareTriplet>> {
  if (!isRecord(container)) {
    return {};
  }

  const versions: Partial<Record<FirmwareType, FirmwareTriplet>> = {};
  for (const [rawType, rawVersion] of Object.entries(container)) {
    const firmwareType = normalizeFirmwareType(rawType);
    if (firmwareType === null) {
      continue;
    }

    const triplet = parseFirmwareTriplet(rawVersion);
    if (triplet !== null) {
      versions[firmwareType] = triplet;
    }
  }

  return versions;
}

function extractInstalledFirmwareVersions(
  versionMetadata: Record<string, unknown> | null | undefined
): Partial<Record<FirmwareType, FirmwareTriplet>> {
  return extractVersionMap(versionMetadata?.versionsByType);
}

function extractExpectedFirmwareVersions(
  versionMetadata: Record<string, unknown> | null | undefined
): Partial<Record<FirmwareType, FirmwareTriplet>> {
  if (!versionMetadata) {
    return {};
  }

  for (const containerKey of EXPECTED_VERSION_CONTAINER_KEYS) {
    const versions = extractVersionMap(versionMetadata[containerKey]);
    if (Object.keys(versions).length > 0) {
      return versions;
    }
  }

  return {};
}

function compareFirmwareTriplets(a: FirmwareTriplet, b: FirmwareTriplet): number {
  if (a.major !== b.major) {
    return a.major - b.major;
  }

  if (a.minor !== b.minor) {
    return a.minor - b.minor;
  }

  return a.revision - b.revision;
}

function getAssetVersionEntries(versionMetadata: Record<string, unknown> | null | undefined): AssetVersionEntry[] | null {
  if (!versionMetadata) {
    return null;
  }

  if (Array.isArray(versionMetadata.assetVersion)) {
    return versionMetadata.assetVersion as AssetVersionEntry[];
  }

  if (Array.isArray(versionMetadata.assetVersions)) {
    return versionMetadata.assetVersions as AssetVersionEntry[];
  }

  return null;
}

function evaluateFirmwareDownloadNeededFromAssetVersionEntries(entries: AssetVersionEntry[] | null): boolean | null {
  if (entries === null) {
    return null;
  }

  let sawExpectedAssetVersion = false;
  for (const entry of entries) {
    if (!isRecord(entry)) {
      continue;
    }

    const local = entry.local;
    const expectedAssetId = isRecord(local) && typeof local.assetId === 'string' ? local.assetId : null;
    const localVersion = getAssetSlotVersion(local, null);
    if (localVersion === null) {
      continue;
    }
    sawExpectedAssetVersion = true;

    const stagedVersion = getAssetSlotVersion(entry.staged, expectedAssetId);
    if (stagedVersion !== null && compareFirmwareTriplets(stagedVersion, localVersion) >= 0) {
      continue;
    }

    const activeVersion = getAssetSlotVersion(entry.active, expectedAssetId);
    if (activeVersion === null || compareFirmwareTriplets(activeVersion, localVersion) < 0) {
      return true;
    }
  }

  return sawExpectedAssetVersion ? false : null;
}

function evaluateFirmwareInstallNeededFromStagedAssetVersionEntries(entries: AssetVersionEntry[] | null): boolean {
  if (entries === null) {
    return false;
  }

  return entries.some((entry) => {
    if (!isRecord(entry)) {
      return false;
    }

    const active = entry.active;
    const staged = entry.staged;
    const expectedAssetId =
      isRecord(active) && typeof active.assetId === 'string'
        ? active.assetId
        : isRecord(staged) && typeof staged.assetId === 'string'
        ? staged.assetId
        : null;
    const activeVersion = getAssetSlotVersion(active, expectedAssetId);
    const stagedVersion = getAssetSlotVersion(staged, expectedAssetId);

    return activeVersion !== null && stagedVersion !== null && compareFirmwareTriplets(activeVersion, stagedVersion) < 0;
  });
}

function getNestedLibraryUpdaterPayload(raw: Record<string, unknown>): Record<string, unknown> | null {
  const nestedProgress = raw.libraryUpdaterProgress;
  if (isRecord(nestedProgress)) {
    return nestedProgress;
  }

  const nestedState = raw.libraryUpdaterState;
  if (isRecord(nestedState)) {
    return nestedState;
  }

  return null;
}

/**
 * Normalizes a flat library-updater payload (e.g. `getLibraryUpdaterProgress().data`) so
 * `state` is populated when the firmware only provides `libraryUpdaterState` as a string.
 */
export function normalizeLibraryUpdaterProgressPayload(raw: Record<string, unknown> | null | undefined): FirmwareVersionsPayload | null {
  if (raw === null || raw === undefined || typeof raw !== 'object') {
    return null;
  }
  const nestedPayload = getNestedLibraryUpdaterPayload(raw);
  const source = nestedPayload ?? raw;
  const stateDirect = typeof source.state === 'string' ? source.state : undefined;
  const legacyFlat = typeof source.libraryUpdaterState === 'string' ? source.libraryUpdaterState : undefined;
  const mergedState = stateDirect && stateDirect.trim() !== '' ? stateDirect : legacyFlat && legacyFlat.trim() !== '' ? legacyFlat : undefined;
  const criticalUpdate = source.criticalUpdate === true || raw.criticalUpdate === true;
  if (mergedState === undefined && !criticalUpdate) {
    return null;
  }
  return {
    ...(mergedState !== undefined ? { state: mergedState } : {}),
    criticalUpdate,
  };
}

export function evaluateLibraryUpdaterState(firmwareVersions: FirmwareVersionsPayload | null | undefined): FirmwareStatusEvaluation {
  const state = firmwareVersions?.state;
  const criticalUpdate = firmwareVersions?.criticalUpdate === true;

  const defaultEvaluation: FirmwareStatusEvaluation = {
    status: RobotActions.FIRMWARE_STATUS.UP_TO_DATE,
    needsDownload: false,
    needsInstall: false,
    criticalUpdate,
  };

  if (criticalUpdate && !isNonEmptyUpdaterPhase(state)) {
    return defaultEvaluation;
  }

  switch (state) {
    case 'ready_to_finalize':
      return {
        ...defaultEvaluation,
        status: RobotActions.FIRMWARE_STATUS.READY_TO_INSTALL,
        needsInstall: true,
      };
    case 'jumping_to_finalize':
    case 'finalizing':
    case 'jumping_after_finalize':
      // These are transient finalize-in-progress phases. There is nothing actionable
      // for the client to do anymore, but we also do not want to regress to
      // UPDATE_AVAILABLE. With the current status enum, treat them as non-actionable.
      return defaultEvaluation;
    // Download need intentionally comes from version metadata, not updater phases.
    // Without usable version metadata, keep these phases non-actionable.
    case 'ready_to_start_update':
    case 'waiting_for_asset':
    case 'erasing_assets':
    case 'not_initialized':
    case 'evaluating':
      return defaultEvaluation;
    default:
      return defaultEvaluation;
  }
}

export function evaluateFirmwareDownloadNeededFromVersions(versionMetadata: Record<string, unknown> | null | undefined): boolean {
  const assetVersionDownloadNeeded = evaluateFirmwareDownloadNeededFromAssetVersionEntries(getAssetVersionEntries(versionMetadata));
  if (assetVersionDownloadNeeded !== null) {
    return assetVersionDownloadNeeded;
  }

  const expectedVersions = extractExpectedFirmwareVersions(versionMetadata);
  if (Object.keys(expectedVersions).length === 0) {
    return false;
  }

  const installedVersions = extractInstalledFirmwareVersions(versionMetadata);
  return FIRMWARE_TYPES.some((firmwareType) => {
    const expectedVersion = expectedVersions[firmwareType];
    if (expectedVersion === undefined) {
      return false;
    }

    const installedVersion = installedVersions[firmwareType];
    if (installedVersion === undefined) {
      return true;
    }

    return compareFirmwareTriplets(installedVersion, expectedVersion) < 0;
  });
}

export function evaluateFirmwareInstallNeededFromStagedVersions(versionMetadata: Record<string, unknown> | null | undefined): boolean {
  return evaluateFirmwareInstallNeededFromStagedAssetVersionEntries(getAssetVersionEntries(versionMetadata));
}

export function evaluateFirmwareStatus(input: FirmwareStatusInput | null): FirmwareStatusEvaluation {
  const updaterEvaluation = evaluateLibraryUpdaterState(input?.updaterState);

  if (updaterEvaluation.needsInstall) {
    return updaterEvaluation;
  }

  if (hasConcreteLibraryUpdaterPhase(input?.updaterState)) {
    const state = input?.updaterState?.state;
    if (state === 'jumping_to_finalize' || state === 'finalizing' || state === 'jumping_after_finalize') {
      return updaterEvaluation;
    }
  }

  const needsDownload = evaluateFirmwareDownloadNeededFromVersions(input?.versionMetadata);
  if (needsDownload) {
    return {
      ...updaterEvaluation,
      status: RobotActions.FIRMWARE_STATUS.UPDATE_AVAILABLE,
      needsDownload: true,
    };
  }

  if (evaluateFirmwareInstallNeededFromStagedVersions(input?.versionMetadata)) {
    return {
      ...updaterEvaluation,
      status: RobotActions.FIRMWARE_STATUS.READY_TO_INSTALL,
      needsInstall: true,
    };
  }

  return updaterEvaluation;
}

export function hasConcreteLibraryUpdaterPhase(firmwareVersions: FirmwareVersionsPayload | null | undefined): boolean {
  return isNonEmptyUpdaterPhase(firmwareVersions?.state);
}

export function evaluatePostTransferLibraryUpdaterState(firmwareVersions: FirmwareVersionsPayload | null | undefined): FirmwareStatusEvaluation {
  if (firmwareVersions?.state === 'ready_to_start_update') {
    return {
      status: RobotActions.FIRMWARE_STATUS.UP_TO_DATE,
      needsDownload: false,
      needsInstall: false,
      criticalUpdate: firmwareVersions?.criticalUpdate === true,
    };
  }

  return evaluateLibraryUpdaterState(firmwareVersions);
}

export default class RobotFirmwareManager {
  robotType: string;

  private firmwareVersions: FirmwareVersionsPayload | null = null;

  private readonly dispatch: AppDispatch;

  private readonly robotDataActions = robotDataSlice.actions;

  private readonly robotCommandActions = robotSlice.actions;

  private _needsDownload = false;

  private _needsInstall = false;

  private _criticalUpdate = false;

  constructor(robotType: string, store: ReduxStoreLike) {
    this.robotType = robotType;
    this.dispatch = store.dispatch;
  }

  setFirmwareVersions = (firmwareVersions: FirmwareVersionsPayload | FirmwareStatusInput | null): void => {
    const input = this.normalizeFirmwareStatusInput(firmwareVersions);
    const normalizedFirmwareVersions = input?.updaterState ?? null;

    this.firmwareVersions = normalizedFirmwareVersions;
    this.dispatch(this.robotDataActions.onUpdateFirmwareVersions(normalizedFirmwareVersions));
    this.dispatch(this.robotCommandActions.onUpdateFirmwareStatus(this.evaluateFirmwareStatus(input)));
  };

  getFirmwareVersions = (): FirmwareVersionsPayload | null => {
    return this.firmwareVersions;
  };

  needsInstall(): boolean {
    return this._needsInstall === true;
  }

  needsDownload(): boolean {
    return this._needsDownload === true;
  }

  hasActionableUpdate(): boolean {
    return this._needsDownload === true || this._needsInstall === true;
  }

  criticalUpdate(): boolean {
    return this._criticalUpdate === true;
  }

  canUpdate(): boolean {
    return this.robotType === ROBOTS.BPRDU.type;
  }

  evaluateFirmwareStatus(firmwareVersions: FirmwareVersionsPayload | FirmwareStatusInput | null): string {
    const evaluation = evaluateFirmwareStatus(this.normalizeFirmwareStatusInput(firmwareVersions));

    this._needsDownload = evaluation.needsDownload;
    this._needsInstall = evaluation.needsInstall;
    this._criticalUpdate = evaluation.criticalUpdate;

    return evaluation.status;
  }

  private normalizeFirmwareStatusInput(firmwareVersions: FirmwareVersionsPayload | FirmwareStatusInput | null): FirmwareStatusInput | null {
    if (firmwareVersions === null) {
      return null;
    }

    if (isRecord(firmwareVersions) && ('updaterState' in firmwareVersions || 'versionMetadata' in firmwareVersions)) {
      return {
        updaterState:
          firmwareVersions.updaterState == null
            ? null
            : normalizeLibraryUpdaterProgressPayload(firmwareVersions.updaterState as Record<string, unknown>),
        versionMetadata: isRecord(firmwareVersions.versionMetadata) ? firmwareVersions.versionMetadata : null,
      };
    }

    return {
      updaterState: normalizeLibraryUpdaterProgressPayload(firmwareVersions as Record<string, unknown>),
      versionMetadata: firmwareVersions as Record<string, unknown>,
    };
  }
}
